import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

export const ItemsPerPageDropdown = ({
    value,
    onChange,
    options = [5, 10, 25, 35, 50, 100]
}: {
    value: number;
    onChange: (val: number) => void;
    options?: number[];
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const [openUpward, setOpenUpward] = useState(false);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

    const updatePosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const estimatedHeight = options.length * 36; // Approx height
            const spaceBelow = window.innerHeight - rect.bottom;
            const shouldOpenUp = spaceBelow < estimatedHeight + 20;

            setOpenUpward(shouldOpenUp);
            setDropdownPosition({
                top: shouldOpenUp
                    ? rect.top + window.scrollY - 4
                    : rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    useEffect(() => {
        if (isOpen) {
            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
        }
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
                dropdownMenuRef.current && !dropdownMenuRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative inline-block w-16">
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full bg-gray-800/50 text-white text-[11px] font-bold px-2 py-1 border border-gray-700 rounded flex items-center justify-between focus:outline-none focus:border-orange-500 transition-colors"
            >
                {value}
                <ChevronDown size={12} className={`ml-1 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && createPortal(
                <div
                    ref={dropdownMenuRef}
                    style={{
                        position: 'absolute',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                        transform: openUpward ? 'translateY(-100%)' : 'none'
                    }}
                    className="z-[9999] bg-gray-900 border border-gray-700 rounded shadow-2xl overflow-hidden py-1"
                >
                    {options.map((option) => (
                        <div
                            key={option}
                            onClick={() => {
                                onChange(option);
                                setIsOpen(false);
                            }}
                            className={`cursor-pointer px-3 py-1.5 text-[11px] font-medium transition-colors ${option === value
                                ? 'bg-orange-600 text-white'
                                : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                                }`}
                        >
                            {option}
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

export default ItemsPerPageDropdown;
